import { jsPDF } from 'jspdf';

const COLOR_PRIMARIO = [32, 69, 61];        // #20453D
const COLOR_SECUNDARIO = [57, 128, 106];    // #39806A
const COLOR_TEXTO = [55, 65, 81];           // gray-700
const COLOR_TEXTO_SUAVE = [148, 156, 165];  // gray-400
const COLOR_ENCABEZADO_TABLA = [243, 244, 246]; // gray-100
const COLOR_FILA_ALTERNA = [250, 250, 250];
const COLOR_SEPARADOR_COLUMNA = [221, 225, 229]; // gray-300, visible sobre blanco y sobre el encabezado

function crearGeometria(orientacion = 'portrait') {
  const MARGEN = 28;
  const ANCHO_PAGINA = orientacion === 'landscape' ? 841.89 : 595.28; // A4 en puntos
  const ALTO_PAGINA = orientacion === 'landscape' ? 595.28 : 841.89;
  return {
    MARGEN,
    ANCHO_PAGINA,
    ALTO_PAGINA,
    ANCHO_UTIL: ANCHO_PAGINA - MARGEN * 2,
    ALTO_FOOTER: 26
  };
}

function formatMonto(monto) {
  return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(fecha) {
  if (!fecha) return '-';
  const [y, m, d] = fecha.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-DO', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatFechaCorta(fecha) {
  if (!fecha) return '-';
  const [y, m, d] = fecha.split('-');
  return `${d}/${m}/${y}`;
}

function asegurarEspacio(doc, geo, estado, y, alturaNecesaria) {
  if (y + alturaNecesaria > geo.ALTO_PAGINA - geo.ALTO_FOOTER) {
    doc.addPage();
    estado.pagina += 1;
    return geo.MARGEN;
  }
  return y;
}

function dibujarEncabezado(doc, geo, filtros, titulo) {
  doc.setFillColor(...COLOR_SECUNDARIO);
  doc.rect(0, 0, geo.ANCHO_PAGINA, 5, 'F');

  doc.setTextColor(...COLOR_PRIMARIO);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text(titulo, geo.MARGEN, 40);

  doc.setTextColor(...COLOR_TEXTO_SUAVE);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const notaComprobante = filtros.solo_con_comprobante ? ' · Solo facturas con comprobante fiscal (NCF)' : '';
  doc.text(`${formatFecha(filtros.fecha_desde)} - ${formatFecha(filtros.fecha_hasta)}${notaComprobante}`, geo.MARGEN, 56);

  const generado = new Date().toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' });
  doc.text(`Generado: ${generado}`, geo.ANCHO_PAGINA - geo.MARGEN, 56, { align: 'right' });

  doc.setDrawColor(...COLOR_ENCABEZADO_TABLA);
  doc.setLineWidth(1);
  doc.line(geo.MARGEN, 68, geo.ANCHO_PAGINA - geo.MARGEN, 68);

  return 90;
}

function dibujarTituloSeccion(doc, geo, estado, y, texto) {
  const yy = asegurarEspacio(doc, geo, estado, y, 26);
  doc.setTextColor(...COLOR_PRIMARIO);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(texto, geo.MARGEN, yy);
  return yy + 14;
}

const TABLA_PADDING_X = 6;
const TABLA_PADDING_Y = 6;
const TABLA_LINE_HEIGHT = 10;

/**
 * Ajusta un valor al ancho real de su columna partiéndolo en líneas
 * (nunca deja que el texto se desborde sobre la columna vecina).
 */
function partirEnLineas(doc, fontSize, valor, anchoColumna) {
  doc.setFontSize(fontSize);
  const anchoDisponible = Math.max(anchoColumna - TABLA_PADDING_X * 2, 12);
  return doc.splitTextToSize(String(valor ?? ''), anchoDisponible);
}

function dibujarSeparadoresColumna(doc, geo, columnas, yPos, altura) {
  doc.setDrawColor(...COLOR_SEPARADOR_COLUMNA);
  doc.setLineWidth(0.5);
  let x = geo.MARGEN;
  columnas.forEach((col, idx) => {
    x += col.width;
    if (idx < columnas.length - 1) {
      doc.line(x, yPos, x, yPos + altura);
    }
  });
}

function dibujarTabla(doc, geo, estado, { y, columnas, filas }) {
  const dibujarEncabezadoTabla = (yPos) => {
    doc.setFont('helvetica', 'bold');
    const lineasPorColumna = columnas.map(col => partirEnLineas(doc, 8, col.titulo.toUpperCase(), col.width));
    const maxLineas = Math.max(...lineasPorColumna.map(l => l.length), 1);
    const alturaEncabezado = maxLineas * TABLA_LINE_HEIGHT + TABLA_PADDING_Y;

    doc.setFillColor(...COLOR_ENCABEZADO_TABLA);
    doc.rect(geo.MARGEN, yPos, geo.ANCHO_UTIL, alturaEncabezado, 'F');
    doc.setTextColor(...COLOR_TEXTO);

    let x = geo.MARGEN;
    columnas.forEach((col, i) => {
      const textX = col.align === 'right' ? x + col.width - TABLA_PADDING_X : x + TABLA_PADDING_X;
      lineasPorColumna[i].forEach((linea, li) => {
        doc.text(linea, textX, yPos + TABLA_PADDING_Y + (li + 1) * TABLA_LINE_HEIGHT - 2, {
          align: col.align === 'right' ? 'right' : 'left'
        });
      });
      x += col.width;
    });

    dibujarSeparadoresColumna(doc, geo, columnas, yPos, alturaEncabezado);
    return yPos + alturaEncabezado;
  };

  // Reserva espacio para encabezado + al menos una fila de una línea antes de empezar
  let cursorY = asegurarEspacio(doc, geo, estado, y, TABLA_LINE_HEIGHT * 2 + TABLA_PADDING_Y * 2);
  cursorY = dibujarEncabezadoTabla(cursorY);

  filas.forEach((fila, idx) => {
    doc.setFont('helvetica', 'normal');
    const lineasPorColumna = columnas.map((col, i) => partirEnLineas(doc, 9, fila[i], col.width));
    const maxLineas = Math.max(...lineasPorColumna.map(l => l.length), 1);
    const alturaFila = maxLineas * TABLA_LINE_HEIGHT + TABLA_PADDING_Y;

    const yAntes = cursorY;
    cursorY = asegurarEspacio(doc, geo, estado, cursorY, alturaFila);
    if (cursorY !== yAntes) {
      cursorY = dibujarEncabezadoTabla(cursorY);
      doc.setFont('helvetica', 'normal');
    }

    if (idx % 2 === 1) {
      doc.setFillColor(...COLOR_FILA_ALTERNA);
      doc.rect(geo.MARGEN, cursorY, geo.ANCHO_UTIL, alturaFila, 'F');
    }

    doc.setTextColor(...COLOR_TEXTO);
    doc.setFontSize(9);
    let x = geo.MARGEN;
    columnas.forEach((col, i) => {
      const textX = col.align === 'right' ? x + col.width - TABLA_PADDING_X : x + TABLA_PADDING_X;
      lineasPorColumna[i].forEach((linea, li) => {
        doc.text(linea, textX, cursorY + TABLA_PADDING_Y + (li + 1) * TABLA_LINE_HEIGHT - 2, {
          align: col.align === 'right' ? 'right' : 'left'
        });
      });
      x += col.width;
    });

    dibujarSeparadoresColumna(doc, geo, columnas, cursorY, alturaFila);

    cursorY += alturaFila;
  });

  doc.setDrawColor(...COLOR_ENCABEZADO_TABLA);
  doc.setLineWidth(1);
  doc.line(geo.MARGEN, cursorY, geo.MARGEN + geo.ANCHO_UTIL, cursorY);

  return cursorY + 20;
}

function dibujarPiePagina(doc, geo) {
  const totalPaginas = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPaginas; i++) {
    doc.setPage(i);
    doc.setTextColor(...COLOR_TEXTO_SUAVE);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('OmniERP', geo.MARGEN, geo.ALTO_PAGINA - 14);
    doc.text(`Página ${i} de ${totalPaginas}`, geo.ANCHO_PAGINA - geo.MARGEN, geo.ALTO_PAGINA - 14, { align: 'right' });
  }
}

/**
 * Genera y descarga el PDF del reporte de ventas a partir de la respuesta
 * de GET /api/reporte/ventas-resumen (ver c_reporte.js: ObtenerReporteVentas).
 */
export default function generarPDFReporteVentas(datos, filtros) {
  if (!datos) return;

  const geo = crearGeometria('portrait');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const estado = { pagina: 1 };

  let y = dibujarEncabezado(doc, geo, filtros, 'Reporte de Ventas');

  // RESUMEN EJECUTIVO
  y = dibujarTituloSeccion(doc, geo, estado, y, 'Resumen Ejecutivo');
  const rg = datos.resumen_general;
  y = dibujarTabla(doc, geo, estado, {
    y,
    columnas: [
      { titulo: 'Indicador', width: geo.ANCHO_UTIL * 0.6, align: 'left' },
      { titulo: 'Valor', width: geo.ANCHO_UTIL * 0.4, align: 'right' }
    ],
    filas: [
      ['Total de Ventas (Neto)', `RD$ ${formatMonto(rg.total_ventas_neto)}`],
      ['Cantidad de Facturas', rg.cantidad_facturas.toLocaleString('es-DO')],
      ['Ticket Promedio', `RD$ ${formatMonto(rg.ticket_promedio)}`],
      ['Dinero Recibido', `RD$ ${formatMonto(datos.dinero_recibido.total_cobrado)}`],
      ['Dinero por Cobrar', `RD$ ${formatMonto(datos.dinero_por_cobrar.total_pendiente)}`],
      [`Ventas al Contado (${datos.ventas_credito_contado.contado.porcentaje}%)`, `RD$ ${formatMonto(datos.ventas_credito_contado.contado.monto_total)}`],
      [`Ventas a Crédito (${datos.ventas_credito_contado.credito.porcentaje}%)`, `RD$ ${formatMonto(datos.ventas_credito_contado.credito.monto_total)}`],
      [`Ventas Gratuitas (${datos.ventas_credito_contado.gratuito.porcentaje}%)`, `RD$ ${formatMonto(datos.ventas_credito_contado.gratuito.monto_total)}`]
    ]
  });

  // POR TIPO DE COMPROBANTE
  y = dibujarTituloSeccion(doc, geo, estado, y, 'Ventas por Tipo de Comprobante');
  y = dibujarTabla(doc, geo, estado, {
    y,
    columnas: [
      { titulo: 'Tipo de Comprobante', width: geo.ANCHO_UTIL * 0.32, align: 'left' },
      { titulo: 'Facturas', width: geo.ANCHO_UTIL * 0.1, align: 'right' },
      { titulo: 'Subtotal', width: geo.ANCHO_UTIL * 0.18, align: 'right' },
      { titulo: 'ITBIS', width: geo.ANCHO_UTIL * 0.15, align: 'right' },
      { titulo: 'Monto Total', width: geo.ANCHO_UTIL * 0.15, align: 'right' },
      { titulo: '%', width: geo.ANCHO_UTIL * 0.1, align: 'right' }
    ],
    filas: datos.por_tipo_comprobante.map(t => [
      t.descripcion_completa,
      String(t.cantidad_facturas),
      `RD$ ${formatMonto(t.monto_bruto)}`,
      `RD$ ${formatMonto(t.monto_impuesto)}`,
      `RD$ ${formatMonto(t.monto_total)}`,
      `${t.porcentaje_del_total}%`
    ])
  });

  // DINERO RECIBIDO
  if (datos.dinero_recibido.por_metodo_pago.length > 0) {
    y = dibujarTituloSeccion(doc, geo, estado, y, 'Dinero Recibido por Método de Pago');
    y = dibujarTabla(doc, geo, estado, {
      y,
      columnas: [
        { titulo: 'Método de Pago', width: geo.ANCHO_UTIL * 0.45, align: 'left' },
        { titulo: 'Transacciones', width: geo.ANCHO_UTIL * 0.2, align: 'right' },
        { titulo: 'Monto Total', width: geo.ANCHO_UTIL * 0.2, align: 'right' },
        { titulo: '%', width: geo.ANCHO_UTIL * 0.15, align: 'right' }
      ],
      filas: datos.dinero_recibido.por_metodo_pago.map(m => [
        m.metodo_pago,
        String(m.cantidad_transacciones),
        `RD$ ${formatMonto(m.monto_total)}`,
        `${m.porcentaje}%`
      ])
    });
  }

  // CUENTAS POR COBRAR
  y = dibujarTituloSeccion(doc, geo, estado, y, 'Cuentas por Cobrar');
  const dpc = datos.dinero_por_cobrar;
  y = dibujarTabla(doc, geo, estado, {
    y,
    columnas: [
      { titulo: 'Estado', width: geo.ANCHO_UTIL * 0.5, align: 'left' },
      { titulo: 'Facturas', width: geo.ANCHO_UTIL * 0.2, align: 'right' },
      { titulo: 'Monto', width: geo.ANCHO_UTIL * 0.3, align: 'right' }
    ],
    filas: [
      ['Vencidas', String(dpc.vencidas.cantidad), `RD$ ${formatMonto(dpc.vencidas.total)}`],
      ['Por Vencer (7 días)', String(dpc.por_vencer_7_dias.cantidad), `RD$ ${formatMonto(dpc.por_vencer_7_dias.total)}`],
      ['Vigentes', String(dpc.vigentes.cantidad), `RD$ ${formatMonto(dpc.vigentes.total)}`],
      ['Total Pendiente', String(dpc.cantidad_facturas_pendientes), `RD$ ${formatMonto(dpc.total_pendiente)}`]
    ]
  });

  dibujarPiePagina(doc, geo);

  doc.save(`reporte-ventas_${filtros.fecha_desde}_${filtros.fecha_hasta}.pdf`);
}

/**
 * Genera y descarga el PDF del detalle de facturas con comprobante fiscal
 * a partir de la respuesta de GET /api/reporte/ventas-detallado (ver
 * c_reporte.js: ObtenerReporteVentasDetallado). Este reporte no tiene tabla
 * en pantalla — los datos solo se ven al exportar. Landscape por la
 * cantidad de columnas.
 */
export function generarPDFReporteVentasDetallado(datos, filtros) {
  if (!datos) return;

  const geo = crearGeometria('landscape');
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
  const estado = { pagina: 1 };

  let y = dibujarEncabezado(doc, geo, filtros, 'Detalle de Facturas con Comprobante Fiscal');

  if (datos.truncado) {
    doc.setTextColor(200, 120, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(
      `Aviso: se muestran las primeras ${datos.facturas.length.toLocaleString('es-DO')} de ${datos.total_facturas.toLocaleString('es-DO')} facturas encontradas.`,
      geo.MARGEN,
      y
    );
    y += 16;
  }

  // Columnas candidatas. Las marcadas `opcional` solo se muestran si tienen
  // algún monto distinto de cero en todo el conjunto filtrado (evita columnas
  // vacías desperdiciando espacio, p.ej. Cheque/Transferencia si el negocio
  // no las usa).
  const TODAS_LAS_COLUMNAS = [
    { clave: 'fecha', titulo: 'Fecha', peso: 0.06, align: 'left', formato: formatFechaCorta },
    { clave: 'rnc', titulo: 'RNC', peso: 0.08, align: 'left', formato: v => v || '' },
    { clave: 'ncf', titulo: 'NCF', peso: 0.10, align: 'left', formato: v => v || '' },
    { clave: 'cliente', titulo: 'Cliente', peso: 0.20, align: 'left', formato: v => v || '' },
    { clave: 'exento', titulo: 'Exento', peso: 0.07, align: 'right', formato: formatMonto, opcional: true },
    { clave: 'gravado', titulo: 'Gravado', peso: 0.08, align: 'right', formato: formatMonto },
    { clave: 'itbis', titulo: 'Itbis', peso: 0.07, align: 'right', formato: formatMonto },
    { clave: 'total', titulo: 'Total', peso: 0.08, align: 'right', formato: formatMonto },
    { clave: 'efectivo', titulo: 'Efectivo', peso: 0.065, align: 'right', formato: formatMonto, opcional: true },
    { clave: 'tarjeta', titulo: 'Tarjeta', peso: 0.065, align: 'right', formato: formatMonto, opcional: true },
    { clave: 'cheque', titulo: 'Cheque', peso: 0.065, align: 'right', formato: formatMonto, opcional: true },
    { clave: 'transferencia', titulo: 'Transferencia', peso: 0.065, align: 'right', formato: formatMonto, opcional: true }
  ];

  const columnasVisibles = TODAS_LAS_COLUMNAS.filter(col => {
    if (!col.opcional) return true;
    return parseFloat(datos.totales[col.clave] || 0) !== 0;
  });

  // Redistribuir el ancho entre las columnas visibles para llenar la página
  // en vez de dejar espacio en blanco donde estaban las columnas ocultas.
  const sumaPesos = columnasVisibles.reduce((s, c) => s + c.peso, 0);
  const columnas = columnasVisibles.map(col => ({
    titulo: col.titulo,
    align: col.align,
    width: geo.ANCHO_UTIL * (col.peso / sumaPesos)
  }));

  const filas = datos.facturas.map(f => columnasVisibles.map(col => col.formato(f[col.clave])));

  const filaTotales = columnasVisibles.map(col => {
    if (col.clave === 'fecha') return 'TOTALES';
    if (['rnc', 'ncf', 'cliente'].includes(col.clave)) return '';
    return formatMonto(datos.totales[col.clave]);
  });
  filas.push(filaTotales);

  dibujarTabla(doc, geo, estado, { y, columnas, filas });

  dibujarPiePagina(doc, geo);

  doc.save(`reporte-ventas-detallado_${filtros.fecha_desde}_${filtros.fecha_hasta}.pdf`);
}
